import type React from "react";
import { useMemo, useState } from "react";

import type { ConnectedProjectDto } from "@/api/types";
import { Drawer } from "@/components/Drawer/Drawer";
import { filterProjectsByFilters } from "@/components/ProjectFilter/filterProjectsByFilters";
import type { ProjectFilterValues } from "@/components/ProjectFilter/ProjectFilter";
import { ProjectFilterDrawerFields } from "@/components/ProjectFilter/ProjectFilterDrawerFields";
import { Button } from "@/design-system/Button";
import { useTranslation } from "@/translations";

interface AskNobiChatFilterDrawerProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  linkedProjects: ConnectedProjectDto[];
  filters: ProjectFilterValues;
  onApply: (filters: ProjectFilterValues) => void;
}

export function AskNobiChatFilterDrawer({
  isOpen,
  onOpenChange,
  linkedProjects,
  filters,
  onApply,
}: AskNobiChatFilterDrawerProps): React.ReactNode {
  const { t } = useTranslation();
  const [draftFilters, setDraftFilters] = useState<ProjectFilterValues>(filters);

  const draftCount = useMemo(
    () => filterProjectsByFilters(linkedProjects, draftFilters).length,
    [linkedProjects, draftFilters],
  );
  const handleOpenChange = (open: boolean) => {
    if (open) {
      setDraftFilters(filters);
    }

    onOpenChange(open);
  };

  const handleApply = () => {
    onApply(draftFilters);
    onOpenChange(false);
  };

  const handleClear = () => {
    onApply({});
    onOpenChange(false);
  };

  return (
    <Drawer.Root title={t("page.ask-nobi-chat.filter-drawer.title")} isOpened={isOpen} onOpenChange={handleOpenChange}>
      <Drawer.Body>
        <section className="flex flex-1 flex-col gap-space-md">
          <ProjectFilterDrawerFields projects={linkedProjects} filters={draftFilters} onChange={setDraftFilters} />
          <p className="mt-auto text-caption text-text-secondary">
            {t("page.ask-nobi-chat.filter-drawer.project-count", { count: draftCount })}
          </p>
        </section>
      </Drawer.Body>
      <Drawer.Actions>
        <Drawer.Close>
          <Button variant="subtle" onClick={handleClear} data-testid="clear-filters-button">
            {t("common.action.clear")}
          </Button>
        </Drawer.Close>
        <Button onClick={handleApply}>{t("common.action.apply")}</Button>
      </Drawer.Actions>
    </Drawer.Root>
  );
}
